import React, { useEffect, useState } from 'react';
import * as QueryString from 'query-string';
import clsx from 'clsx';
import { useParams, useHistory } from 'react-router';
import { Breadcrumbs, CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import ProfileCard from '../../../components/ProfileCard';
import ConnectorExpectationCard from './ConnectorExpectationCard';
import ConnectorEditProfile from './ConnectorEditProfile';
import EditReferralExpectation from './EditReferralExpectation';
import { useAlert, useConnector, useUser } from '../../../contexts';
import { EntReferralOption, Role } from '../../../types/types';
import api from '../../../api/api';
import Button from '../../../components/Button';
import { StyledText } from '../../../components/StyledText';
import ConnectorMessageDialog from '../../../components/InvestorAccordion/ConnectorMessageDialog';
import ConnectorGuide from '../../../components/PublishingGuide/ConnectorGuide';
import MatchCountInfoIcon from '../../../components/MatchCountInfo';
import { tbd } from '../../../lib/utils';
import { connectorProfileSchema } from '../../../lib/validationSchemas';
import ConnectorRequirementsCard from './ConnectorRequirementsCard';
import EditConnectorRequirements from './EditConnectorRequirements';


interface ConnectorProfilePathParams {
  profileId: string
}

const useStyles = makeStyles((theme: Theme) => ({
  viewProfileRoot: {
    paddingTop: theme.spacing(2)
  },
  profilePanel: {
    width: '928px',
    flexGrow: 2
  },
  expectationPanel: {
    width: '440px'
  },
  entBreadcrumbs: {
    padding: theme.spacing(4, 0, 2, 0)
  },
  connectorName: {
    display: 'flex',
    alignItems: 'center',
    justifyConten: 'space-between',
    height: 50,
    marginBottom: theme.spacing(4)
  },
  connectorGuide: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    marginBottom: theme.spacing(3)
  },
  paddingTop4: {
    paddingTop: theme.spacing(4)
  },
  networkCount: {
    display: 'flex',
    flexGrow: 1,
    padding: theme.spacing(0, 4, 0, 4),
    alignItems: 'center',
    paddingTop: 12
  },
  networkNumber: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    lineHeight: '100%',
    '& div:first-child': {
      fontWeight: 500,
      fontSize: 20
    },
    '& div:nth-child(2)': {
      marginLeft: theme.spacing(1)
    }
  },
  matchIcon: {
    marginLeft: theme.spacing(2),
    lineHeight: '100%',
    fontSize: 15,
    height: 15,
    width: 15
  },
  loadingContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

const ConnectorViewProfile = () => {
  const { userInfo, getAccessToken } = useUser();
  const { postNewAlert } = useAlert();
  const history = useHistory();
  const { profile, fetchProfile, updateProfile, getProfileImageProvider, shouldShowIncompleteProfileGuide, editable } = useConnector();
  const { investorCount, matchCount } = history.location.state as any || {};
  const profileImageProvider = getProfileImageProvider(profile.id);
  const params = useParams<ConnectorProfilePathParams>();
  const profileId = parseInt(params.profileId);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingExpectation, setEditingExpectation] = useState(false);
  const [editingDealFilters, setEditingDealFilters] = useState(false);
  const [isMessageModalOpen, setMessageModalOpen] = useState(false);
  const styles = useStyles();

  const queryParams = QueryString.parse(history.location.search);
  const from = queryParams.from as string | undefined;
  const fromId = from ? parseInt(from) : undefined;

  const { companyName, linkedInProfileUrl, introduction, referralExpectation, email } = profile;

  const getDisplayName = () => {
    if ((profile.firstName && profile.lastName) || !editable) {
      return `${profile.firstName || ''} ${profile.lastName || ''}`;
    }
    return 'Connector name';
  };


  const displayName = getDisplayName();
  const displayCompany = editable ? (companyName || 'Company name') : companyName || '';
  const displayIntroduction = editable ? (introduction || tbd) : introduction || '';
  const displayEmail = editable ? (email || tbd) : email || '';
  const displayLinkedIn = editable ? (linkedInProfileUrl || tbd) : linkedInProfileUrl || '';
  const displayReferralExpectation = editable ? (referralExpectation || tbd) : referralExpectation || '';

  const handleEditClick = () => {
    setEditingProfile(true);
  };

  const handleExpectationEditClick = () => {
    setEditingExpectation(true);
  };

  const handleDealFiltersEditClick = () => {
    setEditingDealFilters(true);
  };

  const handleProfileDoneEditing = async (values: any) => {
    const castValues = connectorProfileSchema.cast(values);
    await updateProfile(castValues);
    setEditingProfile(false);
    setEditingExpectation(false);
    setEditingDealFilters(false);
  };

  const openSendMessageModal = () => {
    setMessageModalOpen(true);
  };

  const closeSendMessageModal = () => {
    setMessageModalOpen(false);
  };

  const handleAskReferral = async (values: any) => {
    try {
      if (values.isCorporateReferralChecked) {
        await api.sendPartnerReferrals(await getAccessToken());
      }

      const { investors } = values;
      await api.sendAskReferrals(await getAccessToken(), {
        entMessage: values.message,
        connectorProfileId: profile.id,
        investorsRequested: investors.map((i: EntReferralOption) => i.investorProfileId)
      });
    } catch (e: unknown) {
      console.error(e);
      postNewAlert('An error occurred sending referrals.');
    } finally {
      setMessageModalOpen(false);
    }
  };

  const isEnt = (userInfo && userInfo.role === Role.Ent);

  useEffect(() => {
    if (profileId && profile.id !== profileId) {
      fetchProfile(profileId);
    }
  }, [profileId, profile, fetchProfile]);

  if (profile.id === -1) {
    return <div className={styles.loadingContainer}><CircularProgress /></div>;
  }

  return (
    <div className={styles.viewProfileRoot}>
      {isMessageModalOpen && (
      <ConnectorMessageDialog
        open={isMessageModalOpen}
        handleClose={closeSendMessageModal}
        connectorData={{
          id: profile.id,
          firstName: profile.firstName,
          lastName: profile.lastName,
          companyName: profile.companyName,
          email: profile.email,
          profileImage: profile.profileImageUrl
        }}
        handleSubmit={handleAskReferral}
        preselectedReferral={fromId}
      />
      )}
      <ConnectorEditProfile
        profile={profile}
        open={editingProfile}
        handleClose={() => { setEditingProfile(false); }}
        handleSubmit={handleProfileDoneEditing}
      />
      <EditReferralExpectation
        referralExpectation={profile.referralExpectation}
        open={editingExpectation}
        handleClose={() => { setEditingExpectation(false); }}
        handleSubmit={handleProfileDoneEditing}
      />
      <EditConnectorRequirements
        entrepreneurRevenueRequiredLow={profile.entrepreneurRevenueRequiredLow}
        open={editingDealFilters}
        handleClose={() => { setEditingDealFilters(false); }}
        handleSubmit={handleProfileDoneEditing}
      />
      {isEnt && (
      <div className={styles.entBreadcrumbs}>
        <Breadcrumbs separator='>'>
          <StyledText
            variant='h6'
            color='secondary'
            onClick={() => history.goBack()}
            style={{ cursor: 'pointer' }}
          >
            Matched Investors
          </StyledText>
          <StyledText variant='h6'>{displayName}</StyledText>
        </Breadcrumbs>
      </div>
      )}
      <div className={clsx({ [styles.connectorName]: true, [styles.paddingTop4]: !isEnt })}>
        <StyledText variant='h1' style={{ lineHeight: '100%' }}>{displayName}</StyledText>
        {isEnt
        && (
        <div className={styles.networkCount}>
          <StyledText color='primary' className={styles.networkNumber}>
            <div>{investorCount}</div>
            <div>investors in network</div>
          </StyledText>
          <StyledText color='primary' className={styles.networkNumber}>
            <div>{matchCount}</div>
            <div>matched investors</div>
          </StyledText>
          <div className={styles.matchIcon}>
            <MatchCountInfoIcon />
          </div>
        </div>
        )}
        {isEnt && matchCount > 0 && <Button style={{ fontSize: 18, lineHeight: '100%', alignSelf: 'flex-end' }} onClick={openSendMessageModal}>Send a message</Button>}
      </div>
      {shouldShowIncompleteProfileGuide && (
        <div className={styles.connectorGuide}>
          <ConnectorGuide profile={profile} />
        </div>
      )}
      <Grid container spacing={3}>
        <Grid item className={styles.profilePanel}>
          <ProfileCard
            name={displayName}
            email={displayEmail}
            profileImageProvider={profileImageProvider}
            company={displayCompany}
            linkedIn={displayLinkedIn}
            summary={displayIntroduction}
            editHandler={editable ? handleEditClick : undefined}
            emailClickHandler={isEnt && matchCount > 0 ? openSendMessageModal : undefined}
          />
        </Grid>
        <Grid item style={{ flexGrow: 1 }} className={styles.expectationPanel}>
          <ConnectorExpectationCard content={displayReferralExpectation} editHandler={editable ? handleExpectationEditClick : undefined} />
          <br />
          <ConnectorRequirementsCard entrepreneurRevenueRequiredLow={profile.entrepreneurRevenueRequiredLow} editHandler={editable ? handleDealFiltersEditClick : undefined} />
        </Grid>
      </Grid>
    </div>
  );
};

export default ConnectorViewProfile;
